// FEATURE-SPACE coverage — the trend is corrected/calibrated where samples sit in covariate space
// so points must span the field's covariate distribution (terrain imagery, yield, EC) — the classic
// cLHS/feature-space-coverage result from the DSM literature (Brus 2019): don't put every sample on
// the same landscape position. Method: k-means over a JOINT space = [local metric x,y] ⊕
// [standardized covariates] with the two blocks variance-balanced 50/50, k = the target point
// count; each cluster contributes the candidate nearest its centroid.
import type { FieldCovariate } from "../types/contract";
import { orderNorthSouthWestEast } from "./pointNumbering";
import { cellAdjacency } from "./cellEdges";
import { metresPerDegree } from "./metres";

export interface LngLat {
  lng: number;
  lat: number;
}
// one polygon = its rings (outer first, then holes), each ring = [lng, lat]
export type PolygonRings = [number, number][][];

// ---- deterministic PRNG ----------------------------------------------------------------
export function hashSeed(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---- geometry (local equirectangular meters — plenty accurate at field scale) ----------
export function polygonsOf(g: unknown): PolygonRings[] {
  const out: PolygonRings[] = [];
  const walk = (node: unknown) => {
    if (!node || typeof node !== "object") return;
    const o = node as {
      type?: string;
      features?: unknown[];
      geometry?: unknown;
      coordinates?: unknown;
    };
    if (o.type === "FeatureCollection") (o.features ?? []).forEach(walk);
    else if (o.type === "Feature") walk(o.geometry);
    else if (o.type === "Polygon") out.push(o.coordinates as PolygonRings);
    else if (o.type === "MultiPolygon")
      (o.coordinates as PolygonRings[]).forEach((p) => out.push(p));
  };
  walk(g);
  return out;
}

function refLatOf(polys: PolygonRings[]): number {
  let sum = 0;
  let n = 0;
  for (const p of polys)
    for (const [, lat] of p[0]) {
      sum += lat;
      n++;
    }
  return n ? sum / n : 0;
}

function ringAreaM2(ring: [number, number][], kx: number, ky: number): number {
  let a = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    a +=
      ring[j][0] * kx * (ring[i][1] * ky) - ring[i][0] * kx * (ring[j][1] * ky);
  }
  return Math.abs(a / 2);
}

export function polygonAreaM2(g: unknown): number {
  const polys = polygonsOf(g);
  if (!polys.length) return 0;
  const { kx, ky } = metresPerDegree(refLatOf(polys));
  let area = 0;
  for (const rings of polys) {
    area += ringAreaM2(rings[0], kx, ky);
    for (let h = 1; h < rings.length; h++) area -= ringAreaM2(rings[h], kx, ky);
  }
  return area;
}

function inRing(pt: LngLat, ring: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (
      yi > pt.lat !== yj > pt.lat &&
      pt.lng < ((xj - xi) * (pt.lat - yi)) / (yj - yi) + xi
    )
      inside = !inside;
  }
  return inside;
}

export function pointInPolygons(pt: LngLat, polys: PolygonRings[]): boolean {
  for (const rings of polys) {
    if (inRing(pt, rings[0]) && !rings.slice(1).some((h) => inRing(pt, h)))
      return true;
  }
  return false;
}

// distance (m) from a point to the nearest boundary segment (outer rings AND hole rings)
export function distanceToOutlineM(pt: LngLat, polys: PolygonRings[]): number {
  const { kx, ky } = metresPerDegree(pt.lat);
  const px = pt.lng * kx;
  const py = pt.lat * ky;
  let best = Infinity;
  for (const rings of polys) {
    for (const ring of rings) {
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const ax = ring[j][0] * kx;
        const ay = ring[j][1] * ky;
        const bx = ring[i][0] * kx;
        const by = ring[i][1] * ky;
        const dx = bx - ax;
        const dy = by - ay;
        const len2 = dx * dx + dy * dy;
        const t =
          len2 === 0
            ? 0
            : Math.max(
                0,
                Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2),
              );
        const ex = px - (ax + t * dx);
        const ey = py - (ay + t * dy);
        best = Math.min(best, Math.hypot(ex, ey));
      }
    }
  }
  return best;
}

// Dense in-boundary candidate grid — the fallback when the covariate stack isn't built
// yet (pitch chosen for ~`target` candidates; refined if the boundary is very slim).
export function gridCandidates(polys: PolygonRings[], target = 1200): LngLat[] {
  const area = polygonAreaM2(
    polys.length === 1
      ? { type: "Polygon", coordinates: polys[0] }
      : { type: "MultiPolygon", coordinates: polys },
  );
  if (!area) return [];
  const { kx, ky } = metresPerDegree(refLatOf(polys));
  let west = Infinity;
  let east = -Infinity;
  let south = Infinity;
  let north = -Infinity;
  for (const p of polys) {
    for (const [lng, lat] of p[0]) {
      west = Math.min(west, lng);
      east = Math.max(east, lng);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    }
  }
  let pitch = Math.sqrt(area / target); // meters
  for (let attempt = 0; attempt < 4; attempt++) {
    const out: LngLat[] = [];
    const dLng = pitch / kx;
    const dLat = pitch / ky;
    for (let lat = south + dLat / 2; lat < north; lat += dLat) {
      for (let lng = west + dLng / 2; lng < east; lng += dLng) {
        const pt = { lng, lat };
        if (pointInPolygons(pt, polys)) out.push(pt);
      }
    }
    if (out.length >= Math.min(200, target / 4)) return out;
    pitch /= 2; // slim/tiny boundary — refine
  }
  return [];
}

// A very fine (mis-entered) density is coarsened to stay under `maxPoints` so the browser never
// floods with an unusable order or freezes the placement loop.
export function gridPoints(
  polys: PolygonRings[],
  acresPerSample: number,
  opts: { edgeBufferM?: number; maxPoints?: number } = {},
): LngLat[] {
  if (!polys.length) return [];
  const edgeBufferM = opts.edgeBufferM ?? 10;
  const maxPoints = opts.maxPoints ?? 1500;
  const areaM2 = polygonAreaM2(
    polys.length === 1
      ? { type: "Polygon", coordinates: polys[0] }
      : { type: "MultiPolygon", coordinates: polys },
  );
  if (!areaM2) return [];
  const areaAc = areaM2 / 4046.86;
  let ac = Math.max(0.1, acresPerSample);
  if (areaAc / ac > maxPoints) ac = areaAc / maxPoints; // coarsen an absurd density to a sane point count
  const spacing = Math.sqrt(ac * 4046.86); // meters between grid points
  const { kx, ky } = metresPerDegree(refLatOf(polys));
  let west = Infinity,
    east = -Infinity,
    south = Infinity,
    north = -Infinity;
  for (const p of polys)
    for (const [lng, lat] of p[0]) {
      west = Math.min(west, lng);
      east = Math.max(east, lng);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    }
  const dLng = spacing / kx;
  const dLat = spacing / ky;
  for (const buf of [edgeBufferM, edgeBufferM / 2, 0]) {
    const out: LngLat[] = [];
    for (let lat = south + dLat / 2; lat <= north; lat += dLat) {
      for (let lng = west + dLng / 2; lng <= east; lng += dLng) {
        const pt = { lng, lat };
        if (!pointInPolygons(pt, polys)) continue;
        if (buf > 0 && distanceToOutlineM(pt, polys) < buf) continue;
        out.push(pt);
      }
    }
    if (out.length) return out;
  }
  return [];
}

// design covariate selection Only WITHIN-FIELD-VARYING drivers matter for placing points inside one
// field: terrain (wetness/erosion position), imagery (productivity pattern), yield and EC when
// present plus SSURGO clay content and soil type — zones v2 — soil type is a clas index, so it
// enters the design ONE-HOT via expandCategoricalFeatures, never as a magnitude).
export const CATEGORICAL_DESIGN = /mapunit|taxon|series/i;
export function pickDesignCovariates(catalog: FieldCovariate[]): string[] {
  const withValues = catalog.filter((c) => c.has_values);
  const take = (
    re: RegExp,
    cat: string | null,
    max: number,
    sortYearDesc = false,
  ) => {
    let rows = withValues.filter(
      (c) => re.test(c.key) && (cat === null || c.category === cat),
    );
    if (sortYearDesc)
      rows = [...rows].sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
    return rows.slice(0, max).map((c) => c.key);
  };
  const keys = [
    // the three composite INDICES the behavior-zone builder uses, now offered to the relative zones
    // too high-signal within-field summaries; listed FIRST so the cap never evicts them.
    ...take(/^fertility__index(?!.*_pct)/i, "fertility", 1), // Fertility Index (pre or post — same within-field pattern)
    ...take(/^productivity__index(?!.*_pct)/i, "productivity", 1), // Productivity / Production Index
    ...take(/k_variance$/i, "supply", 1), // Moisture Index (K variance vs samples or model)
    ...take(/dem|elev|slope|twi|hand|curv|relief|rough/i, "topography", 8),
    ...take(/ndvi/i, "satellite", 4, true),
    ...take(/yield/i, "machine", 2, true),
    ...take(/(^|_)ec(_|$)|conduct/i, "ec", 2, true),
    ...take(/clay/i, "ssurgo", 2),
    ...take(CATEGORICAL_DESIGN, "ssurgo", 1),
  ];
  return [...new Set(keys)].slice(0, 22);
}

// One-hot expansion for the categorical design columns (soil type: map unit / series /
// taxonomy ride the stack as class indices — meaningless as magnitudes). Each of the ≤5 most
// frequent classes becomes its own 0/1 column (nulls stay null); numeric columns pass through
// untouched. A one-class field yields a constant dummy, which the downstream zero-variance
// guards drop — correct, since it carries no within-field signal. Shared by the zone design
// (ZoneCreator) and the sampling-point design (SamplePointEditor).
export function expandCategoricalFeatures(
  keys: string[],
  features: (number | null)[][],
  maxClasses = 5,
): (number | null)[][] {
  const out: (number | null)[][] = [];
  features.forEach((col, i) => {
    if (!CATEGORICAL_DESIGN.test(keys[i] ?? "")) {
      out.push(col);
      return;
    }
    const freq = new Map<number, number>();
    for (const v of col)
      if (v != null && Number.isFinite(v)) freq.set(v, (freq.get(v) ?? 0) + 1);
    const classes = [...freq.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, maxClasses)
      .map(([v]) => v);
    for (const cls of classes)
      out.push(
        col.map((v) =>
          v == null || !Number.isFinite(v) ? null : v === cls ? 1 : 0,
        ),
      );
  });
  return out;
}

// ---- the design ------------------------------------------------------------------------
export interface SuggestArgs {
  polys: PolygonRings[];
  candidates: LngLat[];
  // per-COLUMN value arrays aligned to `candidates` (absent/empty = spatial-only design)
  features?: (number | null)[][];
  geoms?: GeoJSON.Geometry[];
  count: number;
  seed: number;
  edgeBufferM?: number;
}

export interface SuggestResult {
  points: LngLat[];
  clusters: {
    lng: number;
    lat: number;
    cluster: number;
    geom?: GeoJSON.Geometry;
  }[];
}
// `used` only guards the fallbacks — member picks are drawn from disjoint sets, so they can't
// collide.
export function pickClusterRepresentatives(
  X: number[][],
  assign: number[],
  centroids: number[][],
): number[] {
  const dim = centroids[0]?.length ?? X[0]?.length ?? 0;
  const d2 = (p: number[], q: number[]) => {
    let s = 0;
    for (let i = 0; i < dim; i++) s += (p[i] - q[i]) ** 2;
    return s;
  };
  const used = new Set<number>();
  const chosen: number[] = [];
  for (let c = 0; c < centroids.length; c++) {
    let best = -1;
    let bestD = Infinity;
    // the cluster's own members first
    for (let i = 0; i < X.length; i++) {
      if (assign[i] !== c || used.has(i)) continue;
      const dd = d2(X[i], centroids[c]);
      if (dd < bestD) {
        bestD = dd;
        best = i;
      }
    }
    // empty cluster → nearest unused candidate anywhere, so we still emit one point per cluster
    if (best < 0) {
      for (let i = 0; i < X.length; i++) {
        if (used.has(i)) continue;
        const dd = d2(X[i], centroids[c]);
        if (dd < bestD) {
          bestD = dd;
          best = i;
        }
      }
    }
    if (best >= 0) {
      used.add(best);
      chosen.push(best);
    }
  }
  return chosen;
}

// Above this component size, the O(n²) true medoid is skipped for the O(n) nearest-to-centroid (which equals the
// squared-distance medoid) — the two agree closely for large compact patches, and it bounds worst-case cost.
const MEDOID_MAX = 1500;

// The MEDOID of a set of members (min total distance to the others) — the most spatially central member, robust to
// concave/irregular patches (unlike nearest-to-centroid, which can fall outside a concavity). Excludes `used`.
function medoidOf(
  members: number[],
  xy: { x: number; y: number }[],
  used: Set<number>,
): number {
  // ALL candidates must be unused — never return a `used` index (that would duplicate a point /
  // drop a cluster's distinct point); an all-used member set → −1 so the caller routes to
  // `maximinUnused`.
  const cand = members.filter((i) => !used.has(i));
  if (cand.length === 0) return -1;
  if (cand.length === 1) return cand[0];
  if (cand.length > MEDOID_MAX) {
    let cx = 0,
      cy = 0;
    for (const i of cand) {
      cx += xy[i].x;
      cy += xy[i].y;
    }
    cx /= cand.length;
    cy /= cand.length;
    let best = cand[0],
      bestD = Infinity;
    for (const i of cand) {
      const dd = (xy[i].x - cx) ** 2 + (xy[i].y - cy) ** 2;
      if (dd < bestD) {
        bestD = dd;
        best = i;
      }
    }
    return best;
  }
  let best = cand[0],
    bestSum = Infinity;
  for (const i of cand) {
    let s = 0;
    for (const j of cand) s += Math.hypot(xy[i].x - xy[j].x, xy[i].y - xy[j].y);
    if (s < bestSum) {
      bestSum = s;
      best = i;
    }
  }
  return best;
}

// Pick ONE representative candidate per cluster: the MEDOID of the cluster's LARGEST CONNECTED
// (rook-4) COMPONENT an agronomic decision to keep each point clearly, visually tied to the zone it
// represents.
export function pickClusterMedoids(
  assign: number[],
  xy: { x: number; y: number }[],
  adjacency: number[][],
  clusterCount: number,
): number[] {
  const byCluster: number[][] = Array.from({ length: clusterCount }, () => []);
  for (let i = 0; i < assign.length; i++) {
    const c = assign[i];
    if (c >= 0 && c < clusterCount) byCluster[c].push(i);
  }
  const used = new Set<number>();
  const chosen: number[] = [];
  for (let c = 0; c < clusterCount; c++) {
    const members = byCluster[c];
    let pick = -1;
    if (members.length) {
      // largest connected component among this cluster's members (BFS over rook-4 adjacency, restricted to members)
      const memberSet = new Set(members);
      const seen = new Set<number>();
      let biggest: number[] = [];
      for (const start of members) {
        if (seen.has(start)) continue;
        const comp: number[] = [];
        const stack = [start];
        seen.add(start);
        while (stack.length) {
          const v = stack.pop()!;
          comp.push(v);
          for (const nb of adjacency[v] ?? [])
            if (memberSet.has(nb) && !seen.has(nb)) {
              seen.add(nb);
              stack.push(nb);
            }
        }
        if (comp.length > biggest.length) biggest = comp;
      }
      // if the "largest component" is a lone cell but the cluster has several (adjacency degenerate / scattered
      // cluster), fall back to the medoid over ALL members rather than an arbitrary singleton
      const target = biggest.length > 1 ? biggest : members;
      pick = medoidOf(target, xy, used);
    }
    if (pick < 0) pick = maximinUnused(xy, used, chosen); // empty cluster
    if (pick >= 0) {
      used.add(pick);
      chosen.push(pick);
    }
  }
  return chosen;
}

// Fallback for an empty cluster: the unused candidate FARTHEST from the nearest already-chosen point (maximin
// spread), so a degenerate cluster still contributes a sensibly-placed point instead of an arbitrary one.
function maximinUnused(
  xy: { x: number; y: number }[],
  used: Set<number>,
  chosen: number[],
): number {
  let best = -1,
    bestScore = -Infinity;
  for (let i = 0; i < xy.length; i++) {
    if (used.has(i)) continue;
    let nearest = Infinity;
    for (const c of chosen) {
      const dd = (xy[i].x - xy[c].x) ** 2 + (xy[i].y - xy[c].y) ** 2;
      if (dd < nearest) nearest = dd;
    }
    const score = chosen.length ? nearest : i === 0 ? 0 : -1; // no picks yet → just take the first unused
    if (score > bestScore) {
      bestScore = score;
      best = i;
    }
  }
  return best;
}

export function suggestSamplingPoints(a: SuggestArgs): LngLat[] {
  return suggestSamplingPointsDetailed(a).points;
}
export function suggestSamplingPointsDetailed(a: SuggestArgs): SuggestResult {
  const buffer = a.edgeBufferM ?? 15;
  if (!a.candidates.length || a.count < 1) return { points: [], clusters: [] };

  // edge buffer, relaxed progressively so small fields never end up empty
  let keep: number[] = [];
  for (const b of [buffer, buffer / 2, 0]) {
    keep = a.candidates
      .map((_, i) => i)
      .filter(
        (i) => b === 0 || distanceToOutlineM(a.candidates[i], a.polys) >= b,
      );
    if (keep.length >= Math.max(a.count * 2, 8)) break;
  }
  if (!keep.length) keep = a.candidates.map((_, i) => i);
  const count = Math.min(a.count, keep.length);

  // joint matrix: geo block (aspect-true local meters, pooled-scaled) ⊕ covariate block
  // (per-column standardized), variance-balanced 50/50
  const { kx, ky } = metresPerDegree(refLatOf(a.polys));
  const xs = keep.map((i) => a.candidates[i].lng * kx);
  const ys = keep.map((i) => a.candidates[i].lat * ky);
  const mean = (v: number[]) => v.reduce((s, x) => s + x, 0) / v.length;
  const mx = mean(xs);
  const my = mean(ys);
  const geoVar =
    mean(xs.map((x) => (x - mx) ** 2)) + mean(ys.map((y) => (y - my) ** 2));
  const gs = geoVar > 0 ? Math.sqrt(geoVar / 2) : 1; // shared scale keeps field aspect true

  const featCols: number[][] = [];
  for (const col of a.features ?? []) {
    const vals = keep.map((i) => col[i]);
    const present = vals.filter(
      (v): v is number => v != null && Number.isFinite(v),
    );
    if (present.length < keep.length / 2) continue; // mostly-missing column: skip
    const m = mean(present);
    const sd = Math.sqrt(mean(present.map((v) => (v - m) ** 2)));
    if (!(sd > 0)) continue; // constant on this field: no signal
    featCols.push(
      vals.map((v) => (v == null || !Number.isFinite(v) ? 0 : (v - m) / sd)),
    ); // impute missing to mean
  }
  const fScale = featCols.length ? Math.sqrt(2 / featCols.length) : 0; // covariate block variance == geo block

  const dim = 2 + featCols.length;
  const X = keep.map((_, r) => {
    const row = new Array<number>(dim);
    row[0] = (xs[r] - mx) / gs;
    row[1] = (ys[r] - my) / gs;
    for (let c = 0; c < featCols.length; c++)
      row[2 + c] = featCols[c][r] * fScale;
    return row;
  });

  // seeded k-means++ then Lloyd
  const rng = mulberry32(a.seed);
  const d2 = (p: number[], q: number[]) => {
    let s = 0;
    for (let i = 0; i < dim; i++) s += (p[i] - q[i]) ** 2;
    return s;
  };
  const centroids: number[][] = [X[Math.floor(rng() * X.length)].slice()];
  const minD = new Array<number>(X.length).fill(Infinity);
  while (centroids.length < count) {
    const last = centroids[centroids.length - 1];
    let total = 0;
    for (let i = 0; i < X.length; i++) {
      minD[i] = Math.min(minD[i], d2(X[i], last));
      total += minD[i];
    }
    let target = rng() * total;
    let pick = X.length - 1;
    for (let i = 0; i < X.length; i++) {
      target -= minD[i];
      if (target <= 0) {
        pick = i;
        break;
      }
    }
    centroids.push(X[pick].slice());
  }

  const assign = new Array<number>(X.length).fill(0);
  for (let iter = 0; iter < 40; iter++) {
    let changed = false;
    for (let i = 0; i < X.length; i++) {
      let best = 0;
      let bestD = Infinity;
      for (let c = 0; c < centroids.length; c++) {
        const dd = d2(X[i], centroids[c]);
        if (dd < bestD) {
          bestD = dd;
          best = c;
        }
      }
      if (assign[i] !== best) {
        assign[i] = best;
        changed = true;
      }
    }
    const sums = centroids.map(() => new Array<number>(dim).fill(0));
    const counts = new Array<number>(centroids.length).fill(0);
    for (let i = 0; i < X.length; i++) {
      counts[assign[i]]++;
      for (let dIdx = 0; dIdx < dim; dIdx++)
        sums[assign[i]][dIdx] += X[i][dIdx];
    }
    for (let c = 0; c < centroids.length; c++) {
      if (counts[c] === 0) {
        // empty cluster: reseed at the point farthest from its own centroid
        let far = 0;
        let farD = -1;
        for (let i = 0; i < X.length; i++) {
          const dd = d2(X[i], centroids[assign[i]]);
          if (dd > farD) {
            farD = dd;
            far = i;
          }
        }
        centroids[c] = X[far].slice();
        changed = true;
      } else {
        for (let dIdx = 0; dIdx < dim; dIdx++)
          centroids[c][dIdx] = sums[c][dIdx] / counts[c];
      }
    }
    if (!changed) break;
  }

  // Without geoms (spatial-only fallback) → the member nearest the joint centroid.
  let chosen: number[];
  if (a.geoms) {
    const xy = keep.map((ki) => ({
      x: a.candidates[ki].lng * kx,
      y: a.candidates[ki].lat * ky,
    }));
    const adjacency = cellAdjacency(keep.map((ki) => a.geoms![ki]));
    chosen = pickClusterMedoids(assign, xy, adjacency, centroids.length);
  } else {
    chosen = pickClusterRepresentatives(X, assign, centroids);
  }

  const points = orderNorthSouthWestEast(
    chosen.map((r) => a.candidates[keep[r]]),
  );
  // the k-means++ cluster each kept candidate landed in — one suggested point sits in each cluster
  const clusters = keep.map((ki, r) => ({
    lng: a.candidates[ki].lng,
    lat: a.candidates[ki].lat,
    cluster: assign[r],
    geom: a.geoms?.[ki],
  }));
  return { points, clusters };
}

export function clusterCandidatesToPoints(a: {
  polys: PolygonRings[];
  candidates: LngLat[];
  features?: (number | null)[][];
  geoms?: GeoJSON.Geometry[];
  points: LngLat[];
  edgeBufferM?: number;
}): { lng: number; lat: number; cluster: number; geom?: GeoJSON.Geometry }[] {
  if (!a.candidates.length || !a.points.length) return [];
  const buffer = a.edgeBufferM ?? 15;
  let keep: number[] = [];
  for (const b of [buffer, buffer / 2, 0]) {
    keep = a.candidates
      .map((_, i) => i)
      .filter(
        (i) => b === 0 || distanceToOutlineM(a.candidates[i], a.polys) >= b,
      );
    if (keep.length >= Math.max(a.points.length * 2, 8)) break;
  }
  if (!keep.length) keep = a.candidates.map((_, i) => i);
  // joint matrix (geo block ⊕ standardized covariate block, variance-balanced 50/50) — same construction as suggest
  const { kx, ky } = metresPerDegree(refLatOf(a.polys));
  const xs = keep.map((i) => a.candidates[i].lng * kx);
  const ys = keep.map((i) => a.candidates[i].lat * ky);
  const mean = (v: number[]) => v.reduce((s, x) => s + x, 0) / v.length;
  const mx = mean(xs);
  const my = mean(ys);
  const geoVar =
    mean(xs.map((x) => (x - mx) ** 2)) + mean(ys.map((y) => (y - my) ** 2));
  const gs = geoVar > 0 ? Math.sqrt(geoVar / 2) : 1;
  const featCols: number[][] = [];
  for (const col of a.features ?? []) {
    const vals = keep.map((i) => col[i]);
    const present = vals.filter(
      (v): v is number => v != null && Number.isFinite(v),
    );
    if (present.length < keep.length / 2) continue;
    const m = mean(present);
    const sd = Math.sqrt(mean(present.map((v) => (v - m) ** 2)));
    if (!(sd > 0)) continue;
    featCols.push(
      vals.map((v) => (v == null || !Number.isFinite(v) ? 0 : (v - m) / sd)),
    );
  }
  const fScale = featCols.length ? Math.sqrt(2 / featCols.length) : 0;
  const dim = 2 + featCols.length;
  const X = keep.map((_, r) => {
    const row = new Array<number>(dim);
    row[0] = (xs[r] - mx) / gs;
    row[1] = (ys[r] - my) / gs;
    for (let c = 0; c < featCols.length; c++)
      row[2 + c] = featCols[c][r] * fScale;
    return row;
  });
  // each served point's cluster centre = the joint row of its nearest kept candidate (by true metres)
  const centroids = a.points.map((p) => {
    let best = 0;
    let bestD = Infinity;
    for (let r = 0; r < keep.length; r++) {
      const dd =
        ((a.candidates[keep[r]].lng - p.lng) * kx) ** 2 +
        ((a.candidates[keep[r]].lat - p.lat) * ky) ** 2;
      if (dd < bestD) {
        bestD = dd;
        best = r;
      }
    }
    return X[best];
  });
  const d2 = (p: number[], q: number[]) => {
    let s = 0;
    for (let i = 0; i < dim; i++) s += (p[i] - q[i]) ** 2;
    return s;
  };
  return keep.map((ki, r) => {
    let best = 0;
    let bestD = Infinity;
    for (let c = 0; c < centroids.length; c++) {
      const dd = d2(X[r], centroids[c]);
      if (dd < bestD) {
        bestD = dd;
        best = c;
      }
    }
    return {
      lng: a.candidates[ki].lng,
      lat: a.candidates[ki].lat,
      cluster: best,
      geom: a.geoms?.[ki],
    };
  });
}

// Consume the SERVED per-point zones (Back-End-Async `automap-suggested-clusters`: one MultiPolygon
// Feature per served point, `properties. num` = its 1-based number) INSTEAD of re-deriving the
// partition — the parity fix.
export function assignCellsToServedZones(
  centroids: LngLat[],
  geoms: (GeoJSON.Geometry | undefined)[] | undefined,
  zonesFc: GeoJSON.FeatureCollection,
): { lng: number; lat: number; cluster: number; geom?: GeoJSON.Geometry }[] {
  const zones = zonesFc.features.map((f, idx) => {
    // `num` is the server's 1-based point number; fall back to feature order for a missing/invalid one
    const raw = f.properties?.num;
    const num = typeof raw === "number" && raw >= 1 ? raw : idx + 1;
    const polys = polygonsOf(f.geometry);
    let w = Infinity,
      s = Infinity,
      e = -Infinity,
      n = -Infinity;
    for (const rings of polys)
      for (const [x, y] of rings[0] ?? []) {
        if (x < w) w = x;
        if (x > e) e = x;
        if (y < s) s = y;
        if (y > n) n = y;
      }
    return { cluster: num - 1, polys, w, s, e, n };
  });
  const out: {
    lng: number;
    lat: number;
    cluster: number;
    geom?: GeoJSON.Geometry;
  }[] = [];
  for (let i = 0; i < centroids.length; i++) {
    const p = centroids[i];
    for (const z of zones) {
      if (p.lng < z.w || p.lng > z.e || p.lat < z.s || p.lat > z.n) continue;
      if (pointInPolygons(p, z.polys)) {
        out.push({
          lng: p.lng,
          lat: p.lat,
          cluster: z.cluster,
          geom: geoms?.[i],
        });
        break;
      }
    }
  }
  return out;
}
